/**
 * SummaryStep Component
 * Step 2 of the tenant deployment dialog - review and confirm deployment
 * Captures screenshot of summary before deployment
 */

import React, { useMemo } from 'react';
import { Text, tokens } from '@fluentui/react-components';
import { SummaryContent, DialogFooter, useDeployWithScreenshot } from '../shared';
import type { SummaryStepProps } from './types';

export const SummaryStep: React.FC<SummaryStepProps> = ({
  summary,
  targetTenantName,
  prefix,
  isRefreshing = false,
  refreshError = null,
  onRetryRefresh,
  onBack,
  onConfirm,
  onClose,
}) => {
  const screenshotName = useMemo(() => {
    const policyNames = summary.selectedRules.map(sr => sr.policy.policyName).join('-');
    return `${targetTenantName}-${policyNames}`.substring(0, 100);
  }, [summary.selectedRules, targetTenantName]);

  const { summaryRef, handleDeploy, deployError, primaryLabel, primaryDisabled } = useDeployWithScreenshot({
    screenshotName,
    screenshotVariant: 'tenant',
    onConfirm,
  });

  // Transform policy rule summaries to the format expected by SummaryContent
  const ruleSummaries = summary.selectedRules.map(({ policy, rules }) => ({
    id: policy.policyId,
    name: policy.policyName,
    rules: rules.map(r => ({ ruleId: r.ruleId, ruleName: r.ruleName })),
  }));

  // Screenshot header content
  const screenshotHeader = (
    <div>
      <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: 4 }}>
        Tenant Deployment Summary
      </Text>
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
        Target: {targetTenantName} | Policies: {summary.selectedRules.length} | Date: {new Date().toLocaleString()}
      </Text>
    </div>
  );

  return (
    <>
      <SummaryContent
        ref={summaryRef}
        ruleSummaries={ruleSummaries}
        totalRulesCount={summary.totalRulesCount}
        sitsExisting={summary.sitsExisting}
        sitsNew={summary.sitsNew}
        labelsExisting={summary.labelsExisting}
        labelsNew={summary.labelsNew}
        isRefreshing={isRefreshing}
        refreshError={refreshError}
        onRetryRefresh={onRetryRefresh}
        labelPrefixNote={
          prefix
            ? `Parent and standalone labels, as well as policies, will be prefixed with "${prefix}" when first created; existing labels are never renamed, and child labels are not prefixed.`
            : undefined
        }
        screenshotHeader={screenshotHeader}
      />
      <DialogFooter
        showBack
        onBack={onBack}
        onCancel={onClose}
        onPrimary={handleDeploy}
        primaryLabel={primaryLabel}
        primaryDisabled={primaryDisabled || isRefreshing || !!refreshError}
      >
        {deployError && (
          <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: 13 }}>{deployError}</Text>
        )}
      </DialogFooter>
    </>
  );
};

export default SummaryStep;
